import React from "react";
import { Link } from "react-router-dom";
import { Heart, MapPin, Star, BadgeCheck } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../context/AuthContext";
import { fileUrl } from "../lib/api";
import { formatPrice } from "../lib/format";
import { ISLAND_NAME } from "../lib/data";
import api from "../lib/api";
import { toast } from "sonner";

export function ListingCard({ listing, onFavToggle, index = 0 }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [fav, setFav] = React.useState(listing.is_favorite);
  const cover = listing.photos?.[0];

  const toggleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error(t("auth.loginToContinue")); return; }
    try {
      const { data } = await api.post(`/favorites/${listing.id}`);
      setFav(data.favorite);
      toast.success(data.favorite ? t("favorites.added") : t("favorites.removed"));
      onFavToggle?.(listing.id, data.favorite);
    } catch { toast.error("Error"); }
  };

  return (
    <Link
      to={`/listing/${listing.id}`}
      data-testid={`listing-card-${listing.id}`}
      className="group block bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-teal-300 hover:shadow-lg hover:shadow-teal-900/5 transition-all cm-fade-up"
      style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-slate-100">
        {cover ? (
          <img src={fileUrl(cover)} alt={listing.title} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-300 text-xs">{t("listing.noPhotos")}</div>
        )}
        {listing.is_boosted && (
          <span className="absolute top-2 left-2 font-mono-badge text-[10px] tracking-wider px-2 py-1 rounded-full bg-[#FF9F1C] text-white shadow">★ {t("home.featured")}</span>
        )}
        {listing.status === "vendido" && (
          <span data-testid={`sold-badge-${listing.id}`} className="absolute top-2 left-2 font-mono-badge text-[10px] tracking-wider px-2 py-1 rounded-full bg-rose-600 text-white shadow">{t("listing.sold")}</span>
        )}
        <button
          data-testid={`fav-btn-${listing.id}`}
          onClick={toggleFav}
          className="absolute top-2 right-2 h-9 w-9 grid place-items-center rounded-full bg-white/90 backdrop-blur hover:bg-white transition-colors shadow-sm"
          aria-label="favorite"
        >
          <Heart className={`h-[18px] w-[18px] ${fav ? "fill-rose-500 text-rose-500" : "text-slate-600"}`} />
        </button>
        <span className="absolute bottom-2 left-2 bg-slate-900/90 text-white px-3 py-1 rounded-full font-bold text-sm font-mono-badge">
          {formatPrice(listing.price, lang)}
        </span>
      </div>
      <div className="p-3">
        <h3 className="font-semibold text-sm text-slate-900 line-clamp-1">{listing.title}</h3>
        <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
          <MapPin className="h-3 w-3 shrink-0" />
          <span className="line-clamp-1">{listing.municipality}, {ISLAND_NAME[listing.island]}</span>
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-[11px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">{t(`conditions.${listing.condition}`)}</span>
          {listing.seller?.is_professional ? (
            <span className="flex items-center gap-1 text-[11px] text-teal-700 font-medium"><BadgeCheck className="h-3.5 w-3.5" />Pro</span>
          ) : listing.seller?.rating > 0 ? (
            <span className="flex items-center gap-0.5 text-[11px] text-slate-600"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />{listing.seller.rating}</span>
          ) : null}
        </div>
      </div>
    </Link>
  );
}
